import { Routes } from '@angular/router'

import { DetailedPermissions } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { resourcePage } from '@flowx/designer/shared/entities/resource-page.entity'
import { DefaultApplicationPermissionsGuard } from '@flowx/designer/shared/guards/application-permissions.guards'
import { DefaultPermissionsGuard } from '@flowx/designer/shared/guards/workspaces-permissions.guards'
import { LayoutOptions } from '@flowx/designer/shared/types/layout.types'

import { CorrectiveActionsRoutes } from './corrective-actions-routes'
import { RuntimeActiveInstancesRoutes } from './runtime-active-instances-routes'
import { RuntimeUiFlowSessionsRoutes } from './runtime-ui-flow-sessions-routes'

import { ExceptionDetailsPageComponent } from '../process/pages/exception-details-page/exception-details-page.component'
import { ExceptionsPageComponent } from '../process/pages/exceptions/exceptions-page.component'
import { RuntimeProcessInstanceRoutes } from '../process/runtime-process-instance-routes'
import { RuntimeReusableUiTemplateInstanceRoutes } from '../reusable-ui-template/runtime-reusable-ui-template-instance-routes'
import { UiFlowInstanceRoutes } from '../ui-flow/ui-flow-instance-routes'

const RUNTIME_CONTENT_ACCESS_RIGHTS: DetailedPermissions = {
  canRead: true,
  canEdit: false,
  canDelete: false,
  canCreate: false,
}

const exceptionPage = resourcePage({ type: 'exception', run: { type: 'exception', param: 'id' } })

const triggerFailurePage = resourcePage({
  type: 'trigger-failure',
  run: { type: 'trigger-failure', param: 'id' },
})

export const ApplicationRuntimeRoutes: Routes = [
  {
    path: '',
    redirectTo: 'builds-list',
    pathMatch: 'full',
  },
  {
    path: 'active-policy',
    data: {
      ...resourcePage({ type: 'active-policy', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_active_policy_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/active-policy/active-policy.component').then(
        ({ FlxActivePolicyComponent }) => FlxActivePolicyComponent
      ),
  },
  {
    path: 'scheduled-processes',
    data: {
      ...resourcePage({ type: 'scheduled-processes', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_active_policy_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/scheduled-processes/schedule.component').then(
        ({ ScheduleComponent }) => ScheduleComponent
      ),
  },
  {
    path: 'manage-triggers',
    data: {
      ...resourcePage({ type: 'manage-triggers', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_active_policy_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/manage-triggers/manage-triggers.component').then(
        ({ ManageTriggersComponent }) => ManageTriggersComponent
      ),
  },
  {
    path: 'overrides',
    data: {
      ...resourcePage({ type: 'config-param-overrides', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_config_param_overrides_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/configuration-parameters-overrides/list/configuration-parameters-overrides-list.component').then(
        ({ FlxConfigurationParametersOverridesListComponent }) =>
          FlxConfigurationParametersOverridesListComponent
      ),
  },
  {
    path: 'runtime-settings',
    data: {
      ...resourcePage({ type: 'runtime-settings', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_config_param_overrides_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/runtime-settings/runtime-settings.component').then(
        ({ RuntimeSettingsComponent }) => RuntimeSettingsComponent
      ),
  },
  {
    path: 'builds-list',
    data: {
      ...resourcePage({ type: 'build', pageType: 'settings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_builds_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/builds-list/builds-list.component').then(
        ({ FlxBuildsListComponent }) => FlxBuildsListComponent
      ),
  },
  {
    path: 'exceptions',
    data: { ...exceptionPage, [LayoutOptions.HAS_SIDENAV]: true },
    component: ExceptionsPageComponent,
    canActivate: [DefaultApplicationPermissionsGuard('process_instances_read')],
  },
  {
    path: 'exceptions/:id',
    data: exceptionPage,
    component: ExceptionDetailsPageComponent,
    canActivate: [DefaultApplicationPermissionsGuard('process_instances_read')],
  },
  {
    path: 'failed-triggers',
    data: { ...triggerFailurePage, [LayoutOptions.HAS_SIDENAV]: true },
    canActivate: [DefaultApplicationPermissionsGuard('process_instances_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/failed-triggers/failed-triggers.component').then(
        ({ FailedTriggersComponent }) => FailedTriggersComponent
      ),
  },
  {
    path: 'failed-triggers/:id',
    data: triggerFailurePage,
    canActivate: [DefaultApplicationPermissionsGuard('process_instances_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/failed-triggers/failed-trigger-details/failed-trigger-details.component').then(
        ({ FailedTriggerDetailsComponent }) => FailedTriggerDetailsComponent
      ),
  },

  {
    path: 'task-manager',
    data: {
      ...resourcePage({ type: 'task-manager', pageType: 'other' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultPermissionsGuard('wks_tasks_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/runtime-task-manager/runtime-task-manager.component').then(
        ({ FlxRuntimeTaskManagerComponent }) => FlxRuntimeTaskManagerComponent
      ),
  },
  {
    path: 'ui-flows',
    data: resourcePage({
      type: 'ui-flow-session',
      run: { type: 'ui-flow-session', param: 'uuid' },
    }),
    children: RuntimeUiFlowSessionsRoutes,
  },
  {
    path: 'data-sources-content',
    data: resourcePage({
      type: 'data-sources-content',
      pageType: 'settings',
    }),
    canActivate: [DefaultPermissionsGuard('wks_builds_read')],
    children: [
      {
        path: '',
        data: { [LayoutOptions.HAS_SIDENAV]: true },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/runtime-content/system-content/system-content.component').then(
            ({ SystemContentComponent }) => SystemContentComponent
          ),
      },
      {
        path: 'data-sources/:resourceId',
        data: { [LayoutOptions.HAS_SIDENAV]: false },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/runtime-content/system-details-runtime-page/system-details-runtime-page.component').then(
            ({ SystemDetailsRuntimePageComponent }) => SystemDetailsRuntimePageComponent
          ),
      },
      {
        path: 'operations/:resourceId/:operationId',
        data: { [LayoutOptions.HAS_SIDENAV]: false, accessRights: RUNTIME_CONTENT_ACCESS_RIGHTS },
        loadComponent: () =>
          import('@flowx/designer/modules/integration-designer/components/operation-details-page/operation-details-page.component').then(
            ({ OperationDetailsPageComponent }) => OperationDetailsPageComponent
          ),
      },
      {
        path: 'kb-stores/:resourceId/:kbStoreUuid',
        data: { [LayoutOptions.HAS_SIDENAV]: false, accessRights: RUNTIME_CONTENT_ACCESS_RIGHTS },
        loadComponent: () =>
          import('@flowx/designer/modules/integration-designer/components/kb-store-details-page/kb-store-details-page.component').then(
            ({ KBStoreDetailsPageComponent }) => KBStoreDetailsPageComponent
          ),
      },
      {
        path: 'data-sources',
        pathMatch: 'full',
        redirectTo: '',
      },
    ],
  },
  {
    path: 'ml-models',
    data: resourcePage({
      type: 'ml-models',
      pageType: 'settings',
    }),
    canActivate: [DefaultPermissionsGuard('wks_builds_read')],
    children: [
      {
        path: '',
        data: { [LayoutOptions.HAS_SIDENAV]: true },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/runtime-content/ml-models/runtime-ml-models.component').then(
            ({ RuntimeMlModelsComponent }) => RuntimeMlModelsComponent
          ),
      },
      {
        path: ':resourceDefinitionId',
        data: { [LayoutOptions.HAS_SIDENAV]: false },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/runtime-content/ml-models/runtime-ml-model-detail.component').then(
            ({ RuntimeMlModelDetailComponent }) => RuntimeMlModelDetailComponent
          ),
      },
      {
        path: ':resourceDefinitionId/versions/:trainedVersionId',
        data: { [LayoutOptions.HAS_SIDENAV]: false },
        loadComponent: () =>
          import('@flowx/designer/modules/integration-designer/ml-models/components/ml-trained-version-page/ml-trained-version-page.component').then(
            ({ MlTrainedVersionPageComponent }) => MlTrainedVersionPageComponent
          ),
      },
    ],
  },
  {
    path: ':appVersionId/processes',
    data: resourcePage({
      type: 'process-instance',
      run: { type: 'process-instance', param: ['processInstanceUuid', 'processUuid'] },
    }),
    children: RuntimeProcessInstanceRoutes,
  },
  {
    path: ':appVersionId/reusable-resources',
    data: resourcePage({ type: 'reusable-template', idParam: 'reusableTemplateResourceId' }),
    children: RuntimeReusableUiTemplateInstanceRoutes,
  },
  {
    path: ':appVersionId/ui-flow',
    data: resourcePage({
      type: 'ui-flow-session',
      run: { type: 'ui-flow-session', param: 'uiFlowSessionUuid' },
    }),
    children: UiFlowInstanceRoutes,
  },
  {
    path: 'active-instances',
    data: resourcePage({
      type: 'process-instance',
      run: { type: 'process-instance', param: 'uuid' },
    }),
    children: RuntimeActiveInstancesRoutes,
  },
  {
    path: 'corrective-actions',
    data: resourcePage({ type: 'corrective-actions', pageType: 'other' }),
    canActivate: [DefaultPermissionsGuard('wks_corrective_actions_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/corrective-actions/main/main-corrective-actions.component').then(
        ({ MainCorrectiveActionsComponent }) => MainCorrectiveActionsComponent
      ),
    children: CorrectiveActionsRoutes,
  },
  {
    path: 'workflow-instances',
    data: {
      ...resourcePage({ type: 'workflow-instance', pageType: 'run_list' }),
      [LayoutOptions.HAS_SIDENAV]: true,
    },
    canActivate: [DefaultApplicationPermissionsGuard('process_instances_read')],
    loadComponent: () =>
      import('@flowx/designer/modules/projects/pages/workflow-instances-page/workflow-instances-page.component').then(
        ({ WorkflowInstancesPageComponent }) => WorkflowInstancesPageComponent
      ),
  },
]
